import { useEffect, useState } from 'react';
import { Copy, Check, Play, RotateCcw, Maximize2, ArrowDownUp, Palette, Hash, Crown, WifiOff, Bot, Trophy, Plus, X } from 'lucide-react';
import { useStore } from '../store';
import { send } from '../net';
import { arrangeRackWithGaps } from '../sort';
import { play as playSfx, isMuted, setMuted } from '../audio';
import { Volume2, VolumeX } from 'lucide-react';
import { checkInitialMeld } from '@rummikub/engine';
import { PhaserGame, getPhaserGame } from '../phaser/PhaserGame';
import type { GameScene } from '../phaser/GameScene';
import { CountdownRing } from './CountdownRing';

function useTick() {
  const [, setT] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setT((x) => x + 1), 250);
    return () => clearInterval(i);
  }, []);
}

export function GameBoard() {
  const remote = useStore((s) => s.remote)!;
  const userId = useStore((s) => s.userId);
  const draftBoard = useStore((s) => s.draftBoard);
  const draftRack = useStore((s) => s.draftRack);
  const setDraft = useStore((s) => s.setDraft);
  const gameOver = useStore((s) => s.gameOver);
  const errorMsg = useStore((s) => s.errorMsg);
  const [copied, setCopied] = useState(false);
  useTick();

  const me = remote.players.find((p) => p.id === userId)!;
  const isMyTurn = remote.currentTurnPlayerId === userId && remote.status === 'PLAYING';
  const isHost = remote.hostId === userId;
  const turnPlayer = remote.players.find((p) => p.id === remote.currentTurnPlayerId);
  const limitSec = remote.settings.turnTimeLimit;
  const deadline = remote.turnStartTime ? remote.turnStartTime + limitSec * 1000 : null;
  const secondsLeft = deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : null;

  const initialCheck = !me.hasMelted
    ? checkInitialMeld(
        draftBoard,
        { board: remote.turnInitialBoard ?? [], playerRack: me.rack, hasMelted: false },
        remote.settings.allowJokerInInitialMeld,
      )
    : null;

  const syncThen = (cb: () => void) => {
    send('moveTile', { board: draftBoard, rack: draftRack });
    setTimeout(cb, 60);
  };
  const handleEndTurn = () => { playSfx('meldSuccess'); syncThen(() => send('endTurn')); };
  const handleDraw = () => { playSfx('drawTile'); useStore.getState().markForceSync(); send('resetTurn'); setTimeout(() => { useStore.getState().markForceSync(); send('drawTile'); }, 60); };
  const handleUndo = () => { playSfx('undo'); useStore.getState().markForceSync(); send('undoStep'); };
  const handleResetTurn = () => { playSfx('resetTurn'); useStore.getState().markForceSync(); send('resetTurn'); };
  const [muted, setMutedState] = useState(isMuted());
  const toggleMute = () => { const m = !muted; setMuted(m); setMutedState(m); };
  // 789 = 順子優先 → 依顏色分組後按數字排列（同色連號會相鄰）
  // 777 = 群組優先 → 依數字分組後按顏色排列（同數字不同色會相鄰）
  const handleSort = (mode: '789' | '777') => {
    const { slotMap, melds, ordered } = arrangeRackWithGaps(draftRack, mode);
    setDraft(draftBoard, ordered, useStore.getState().draftLayout);
    const game = getPhaserGame();
    const scene = game?.scene.getScene('game') as GameScene | undefined;
    scene?.applySortLayoutWithGaps?.(slotMap, melds);
    playSfx('tileSwap');
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  const toggleFullscreen = () => {
    const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      (document.exitFullscreen?.() ?? Promise.resolve())
        ?.catch(() => {});
      doc.webkitExitFullscreen?.();
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => alertIOSFullscreenLimitation());
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else {
      alertIOSFullscreenLimitation();
    }
  };
  const alertIOSFullscreenLimitation = () => {
    useStore.getState().setError('iOS Safari 不支援網頁全螢幕；可從分享選單「加入主畫面」取得無瀏覽器介面體驗');
    setTimeout(() => useStore.getState().setError(null), 5000);
  };

  // 改用 turnInitialBoard 比對：本回合是否動過 = 桌面 vs 回合起點 不同
  const initBoard = remote.turnInitialBoard ?? [];
  const touchedBoard = JSON.stringify(draftBoard) !== JSON.stringify(initBoard);
  const canEndTurn = isMyTurn && touchedBoard;

  // ===== WAITING =====
  if (remote.status === 'WAITING') {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-6 overflow-auto">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-emerald-500/20 blur-[120px] rounded-full" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/20 blur-[120px] rounded-full" />
        </div>
        <div className="relative w-full max-w-md fade-in-up">
          <div className="glass rounded-3xl p-8 shadow-2xl space-y-5">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-1">等待玩家加入</h2>
              <p className="text-slate-400 text-sm">
                {remote.players.length} / 4 已就座
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {remote.players.map((p) => {
                const isBot = p.id.startsWith('bot-');
                return (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      isBot ? 'bg-gradient-to-br from-amber-500 to-yellow-600 text-amber-950' : 'bg-gradient-to-br from-emerald-400 to-blue-500 text-slate-900'
                    }`}>
                      {isBot ? <Bot className="w-5 h-5" /> : p.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold flex items-center gap-1.5">
                        {p.name}
                        {p.id === remote.hostId && <Crown className="w-4 h-4 text-yellow-400" />}
                        {p.id === userId && <span className="text-xs text-emerald-400">（你）</span>}
                        {isBot && <span className="text-[10px] bg-amber-500/30 text-amber-200 px-1.5 py-0.5 rounded">AI</span>}
                      </div>
                    </div>
                    {isHost && isBot && (
                      <button
                        onClick={() => send('removeBot', { botId: p.id })}
                        className="p-1.5 rounded-lg bg-red-900/40 hover:bg-red-800/60 text-red-300"
                        title="移除 AI"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
              {Array.from({ length: 4 - remote.players.length }).map((_, i) => (
                <div key={`empty-${i}`} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900/20 border border-slate-800 border-dashed text-slate-600">
                  <div className="w-10 h-10 rounded-full border-2 border-slate-700 border-dashed flex items-center justify-center" />
                  <div>空位</div>
                  {isHost && i === 0 && remote.players.length < 4 && (
                    <button
                      onClick={() => send('addBot')}
                      className="ml-auto px-3 py-1.5 rounded-lg bg-amber-600/30 hover:bg-amber-600/50 text-amber-200 text-xs flex items-center gap-1 border border-amber-600/40"
                    >
                      <Plus className="w-3.5 h-3.5" /> 加入 AI
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="pt-2 space-y-3">
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800">
                <span className="text-xs text-slate-500 uppercase tracking-wider">房號</span>
                <span className="font-mono text-yellow-400 font-bold">{remote.roomId}</span>
                <button
                  onClick={copyShare}
                  className="ml-auto btn-glow btn-secondary px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  {copied ? '已複製' : '複製連結'}
                </button>
              </div>

              {isHost ? (
                <button
                  onClick={() => send('startGame')}
                  disabled={remote.players.length < 2}
                  className="btn-glow btn-primary w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Play className="w-5 h-5" />
                  {remote.players.length < 2 ? '需要至少 2 人' : '開始遊戲'}
                </button>
              ) : (
                <div className="text-center text-slate-400 text-sm py-3 px-4 rounded-xl bg-slate-900/40">
                  等待房主按下開始…
                </div>
              )}
            </div>

            {errorMsg && (
              <div className="bg-red-900/40 border border-red-500/30 text-red-200 px-4 py-2 rounded-xl text-sm">
                {errorMsg}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== PLAYING =====
  return (
    <>
      {/* Phaser 牌桌（底層，fixed 全螢幕，由 Phaser 自行縮放） */}
      <PhaserGame />

      {/* 左側玩家清單（垂直；手機只顯示頭像） */}
      <div className="player-panel fixed top-0 left-0 z-20 p-2 sm:p-3 flex flex-col gap-1.5 sm:gap-3 pointer-events-none max-w-[40vw]">
        {remote.players.map((p) => {
          const active = p.id === remote.currentTurnPlayerId;
          const isMe = p.id === userId;
          return (
            <div key={p.id} className="pointer-events-auto flex items-center gap-2 sm:gap-3">
              <div className={[
                'relative w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center font-bold text-white text-base sm:text-xl shadow-xl shrink-0',
                active ? 'ring-2 sm:ring-4 ring-cyan-400 shadow-[0_0_24px_rgba(34,211,238,0.5)]' : '',
                isMe ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-slate-500 to-slate-700',
              ].join(' ')}>
                {p.name.slice(0, 1).toUpperCase()}
                {p.id === remote.hostId && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-yellow-500 flex items-center justify-center shadow">
                    <Crown className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-yellow-900" />
                  </div>
                )}
                {p.status === 'OFFLINE' && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-amber-500 flex items-center justify-center border-2 border-blue-900">
                    <Bot className="w-2 h-2 sm:w-3 sm:h-3 text-blue-900" />
                  </div>
                )}
                {/* 手機版：張數顯示在頭像右下 */}
                <div className="sm:hidden absolute -bottom-1 -left-1 px-1 rounded-full bg-blue-900/90 border border-cyan-400 text-[10px] font-bold text-cyan-100">
                  {p.rackCount}
                </div>
              </div>
              {/* 桌面版：完整資訊 */}
              <div className="hidden sm:block text-white">
                <div className="text-sm font-bold flex items-center gap-1 truncate max-w-[140px]">
                  {p.name}{isMe && <span className="text-xs text-cyan-300">（你）</span>}
                </div>
                <div className="text-xs text-blue-200 flex items-center gap-1">
                  <span className="px-1.5 py-0.5 rounded bg-white/20 font-bold">{p.rackCount}</span>
                  {p.hasMelted && <span>已破冰</span>}
                  {p.status === 'OFFLINE' && <><WifiOff className="w-2.5 h-2.5" /> AI</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 右側面板：手機只保留倒數環 + 牌堆數字 */}
      <div className="fixed top-2 right-2 sm:top-3 sm:right-3 z-20 flex flex-col items-end gap-2 sm:gap-3 pointer-events-none">
        <div className="glass-strong rounded-2xl px-2.5 py-1.5 sm:px-4 sm:py-2 flex items-center gap-2 sm:gap-3 pointer-events-auto">
          <CountdownRing secondsLeft={secondsLeft} total={limitSec} />
          <div className="hidden sm:block text-right text-white">
            <div className="text-[10px] text-blue-200 uppercase tracking-wider">當前回合</div>
            <div className="font-bold text-sm">{turnPlayer?.name}</div>
          </div>
        </div>
        <div className="glass-strong rounded-2xl px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-1.5 sm:gap-2 pointer-events-auto text-white">
          <Hash className="hidden sm:block w-4 h-4 text-blue-200" />
          <span className="hidden sm:inline text-sm">牌堆</span>
          <span className="font-black text-base sm:text-xl">{remote.poolCount}</span>
        </div>
        {remote.lastRoundCounter !== null && (
          <div className="glass-strong rounded-xl px-2 py-1 sm:px-4 sm:py-2 text-yellow-300 text-[10px] sm:text-xs font-bold pointer-events-auto">
            最後 {remote.lastRoundCounter} 回合
          </div>
        )}
      </div>

      {/* 破冰徽章 */}
      {!me.hasMelted && isMyTurn && initialCheck && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
          <div
            className={[
              'px-5 py-2 rounded-full text-sm font-bold shadow-xl flex items-center gap-2',
              initialCheck.meetsThreshold
                ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white'
                : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white',
            ].join(' ')}
          >
            <span className="opacity-80 text-xs uppercase tracking-wider">破冰</span>
            <span className="text-lg">{initialCheck.score}</span>
            <span className="opacity-60">/</span>
            <span className="opacity-60">30</span>
            {initialCheck.meetsThreshold ? (
              <Check className="w-4 h-4" />
            ) : (
              <span className="opacity-80 text-xs">差 {30 - initialCheck.score}</span>
            )}
          </div>
        </div>
      )}

      {/* 底部動作列：手機橫向自動切換到右側垂直 */}
      <div className="action-bar-wrap fixed bottom-0 inset-x-0 z-20 p-2 pointer-events-none">
        <div className="action-bar-inner mx-auto max-w-5xl glass-strong rounded-2xl px-3 py-2 flex flex-wrap items-center justify-center sm:justify-between gap-2 pointer-events-auto">
          <div className="flex gap-1.5 flex-wrap justify-center">
            <button
              onClick={() => handleSort('789')}
              className="btn-glow btn-secondary px-3 py-1.5 rounded-lg text-sm font-bold whitespace-nowrap"
              title="依順子排序"
            >789</button>
            <button
              onClick={() => handleSort('777')}
              className="btn-glow btn-secondary px-3 py-1.5 rounded-lg text-sm font-bold whitespace-nowrap"
              title="依群組排序"
            >777</button>
            <button
              onClick={toggleMute}
              className="btn-glow btn-secondary px-2.5 py-1.5 rounded-lg flex items-center justify-center"
              title={muted ? '開啟音效' : '靜音'}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={toggleFullscreen}
              className="btn-glow btn-secondary px-2.5 py-1.5 rounded-lg flex items-center justify-center"
              title="全螢幕"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          {isMyTurn ? (
            <div className="flex gap-1.5 flex-wrap justify-center">
              <button
                onClick={handleUndo}
                className="btn-glow btn-secondary px-2.5 py-1.5 rounded-lg flex items-center justify-center relative"
                title="上一步"
              >
                <RotateCcw className="w-5 h-5" />
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black pl-[1px] pt-[1px]">
                  1
                </span>
              </button>
              <button
                onClick={handleResetTurn}
                className="btn-glow btn-secondary px-2.5 py-1.5 rounded-lg flex items-center justify-center"
                title="全部返回"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
              <button
                onClick={handleDraw}
                className="btn-glow btn-danger px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1 whitespace-nowrap"
              >
                <ArrowDownUp className="w-4 h-4" /> {remote.poolCount === 0 ? 'Pass' : '抽牌'}
              </button>
              <button
                onClick={handleEndTurn}
                disabled={!canEndTurn}
                className="btn-glow btn-primary px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <Play className="w-4 h-4" /> 結束
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-300 whitespace-nowrap">
              等待 <span className="text-white font-semibold">{turnPlayer?.name}</span> 出牌…
            </div>
          )}
        </div>
      </div>

      {errorMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 fade-in-up">
          <div className="glass-strong rounded-xl px-5 py-3 border border-red-500/40 text-red-200 shadow-2xl flex items-center gap-2">
            {errorMsg}
          </div>
        </div>
      )}

      {gameOver && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="glass-strong rounded-3xl p-8 max-w-md w-full shadow-2xl fade-in-up">
            <div className="text-center mb-6">
              <Trophy className="w-16 h-16 mx-auto mb-3 text-yellow-400" strokeWidth={1.5} />
              <h2 className="text-3xl font-black">
                {gameOver.trigger === 'RACK_EMPTY' ? '遊戲結束' : '牌堆耗盡'}
              </h2>
            </div>
            <div className="space-y-2 mb-6">
              {gameOver.results.map((r, i) => {
                const p = remote.players.find((x) => x.id === r.userId);
                return (
                  <div
                    key={r.userId}
                    className={[
                      'flex items-center justify-between px-4 py-3 rounded-xl fade-in-up',
                      r.isWinner
                        ? 'bg-gradient-to-r from-yellow-600/40 to-amber-600/40 ring-1 ring-yellow-400/50'
                        : 'bg-slate-800/50',
                    ].join(' ')}
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center font-bold text-slate-900 text-sm">
                        {p?.name.slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold flex items-center gap-1">
                          {p?.name}
                          {r.isWinner && <Trophy className="w-4 h-4 text-yellow-400" />}
                        </div>
                        <div className="text-xs text-slate-400">殘分 {r.residual}</div>
                      </div>
                    </div>
                    <div className={`text-xl font-black ${r.score >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {r.score >= 0 ? '+' : ''}{r.score}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              {isHost && (
                <button
                  onClick={() => { useStore.getState().setGameOver(null); send('newGame'); }}
                  className="btn-glow btn-primary flex-1 py-3 rounded-xl font-bold"
                >
                  再來一局
                </button>
              )}
              <button onClick={() => location.reload()} className="btn-glow btn-secondary flex-1 py-3 rounded-xl font-bold">
                回到大廳
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
